<script setup lang="ts">
/**
 * One measurement step (Measurements 02–09, 12–14, 16–19).
 * Desktop: the Measurement field (focused, typed — "a ruler is awkward with a mouse")
 * + cm/in toggles, the Measure guide 280×400 on the right.
 * Mobile: the guide figure, the instruction, Quiz/Ruler (vertical for Height); tapping
 * the value swaps the ruler for the typed field (18 Type a value).
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairQuizRuler } from '@/ui/patterns/FairQuiz'
import { FairToggleItem } from '@/ui/primitives/FairToggleItem'
import FairProfileV2MeasureGuide from '@/widgets/profile/fair-v2/FairProfileV2MeasureGuide.vue'
import FairProfileV2MeasurementField from '@/widgets/profile/fair-v2/FairProfileV2MeasurementField.vue'
import {
  parseFairProfileV2Input,
  type FairProfileV2Body,
  type FairProfileV2MeasureKey,
  type FairProfileV2Units,
} from '@/widgets/profile/fair-v2/fair-profile-v2-measure.data'
import { sizesQuizRulerOrientation } from '../sizes-quiz.data'

const props = defineProps<{
  fieldKey: FairProfileV2MeasureKey
  body: FairProfileV2Body
  /** In the shown units; null — nothing entered. */
  modelValue: number | null
  unit: string
  units: FairProfileV2Units
  unitLabels: Record<FairProfileV2Units, string>
  /** Ruler range in the shown units. */
  min: number
  max: number
  step: number
  warning: string
  compact: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
  'update:units': [value: FairProfileV2Units]
}>()

const { t } = useI18n()

const label = computed(() => t(`profile.sizes_page.fields.${props.fieldKey}`))
const guide = computed(() => t(`profile.sizes_page.guide.${props.fieldKey}`))
const orientation = computed(() => sizesQuizRulerOrientation(props.fieldKey))
const isHeight = computed(() => orientation.value === 'vertical')

/** Mobile: the typed field replaces the ruler after a tap on the value. */
const typing = ref(false)
watch(
  () => props.fieldKey,
  () => {
    typing.value = false
  },
)

function handleTyped(raw: string) {
  const parsed = parseFairProfileV2Input(raw)
  if (parsed === 'invalid') return
  emit('update:modelValue', parsed)
}

const inputId = computed(() => `sizes-quiz-${props.fieldKey}`)
</script>

<template>
  <div
    class="sizes-quiz-field"
    :class="{ 'sizes-quiz-field--compact': compact, 'sizes-quiz-field--height': isHeight }"
  >
    <!-- Desktop: Row (Left 320 + guide 280, gap 40) -->
    <template v-if="!compact">
      <div class="sizes-quiz-field__left">
        <FairProfileV2MeasurementField
          :key="`${fieldKey}-${units}`"
          :input-id="inputId"
          :label="label"
          :value="modelValue"
          :unit="unit"
          :warning="warning"
          autofocus
          @input="handleTyped"
        />
        <div
          class="sizes-quiz-field__units"
          role="radiogroup"
          :aria-label="t('profile.sizes_page.units')"
        >
          <FairToggleItem
            v-for="option in (['metric', 'imperial'] as const)"
            :key="option"
            :label="unitLabels[option]"
            :selected="units === option"
            @click="emit('update:units', option)"
          />
        </div>
      </div>
      <FairProfileV2MeasureGuide
        class="sizes-quiz-field__guide"
        :body="body"
        :active="fieldKey"
        :title="label"
        :text="guide"
      />
    </template>

    <!-- Mobile: Stack (figure, instruction, ruler) / Height: Row (figure + vertical ruler) -->
    <template v-else>
      <div class="sizes-quiz-field__stack">
        <div
          class="sizes-quiz-field__figure"
          aria-hidden="true"
        >
          <FairProfileV2MeasureGuide
            :body="body"
            :active="fieldKey"
            :title="label"
            :text="guide"
          />
        </div>
        <p
          v-if="!isHeight"
          class="sizes-quiz-field__instruction"
        >
          {{ guide }}
        </p>
        <FairProfileV2MeasurementField
          v-if="typing"
          :key="`${fieldKey}-${units}-typed`"
          class="sizes-quiz-field__typed"
          :input-id="inputId"
          :label="label"
          :value="modelValue"
          :unit="unit"
          :warning="warning"
          autofocus
          @input="handleTyped"
        />
        <FairQuizRuler
          v-else
          :model-value="modelValue"
          :min="min"
          :max="max"
          :step="step"
          :unit="unit"
          :orientation="orientation"
          :aria-label="label"
          :type-label="t('sizes_quiz.type_value')"
          @update:model-value="emit('update:modelValue', $event)"
          @type="typing = true"
        />
      </div>
      <div
        class="sizes-quiz-field__units"
        role="radiogroup"
        :aria-label="t('profile.sizes_page.units')"
      >
        <FairToggleItem
          v-for="option in (['metric', 'imperial'] as const)"
          :key="option"
          :label="unitLabels[option]"
          :selected="units === option"
          @click="emit('update:units', option)"
        />
      </div>
      <!-- 02 Height — untouched: the hint sits under the units. -->
      <p
        v-if="isHeight && modelValue === null && !typing"
        class="sizes-quiz-field__hint"
      >
        {{ t('sizes_quiz.slide_hint') }}
      </p>
    </template>
  </div>
</template>
